import { useEffect, useState } from "react";
import axios from "axios";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "";
const fmtDT   = d => d ? new Date(d).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "";

const STATUS_DOT = { "Pick Up Request":"#7c3aed","Pick Up Accepted":"#4f46e5","Barcode Generated":"#0891b2","Picked Up":"#0284c7","In Transit":"#1d4ed8","Received":"#475569","Repairing":"#d97706","In Dispatch":"#ea580c","Out for Delivery":"#059669","Delivered":"#16a34a" };
const STATUS_EMOJI = { "Pick Up Request":"📞","Pick Up Accepted":"📅","Barcode Generated":"🏷️","Picked Up":"📦","In Transit":"🚚","Received":"🏪","Repairing":"🔧","In Dispatch":"📮","Out for Delivery":"🛵","Delivered":"✅" };

export default function BulkPrint() {
  const [jobs,    setJobs]    = useState([]);
  const [sc,      setSC]      = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const jobIds  = params.get("jobs")?.split(",").filter(Boolean) || [];
    if (!jobIds.length) { setError("No jobs selected"); setLoading(false); return; }

    Promise.all([
      axios.get(`${API}/settings`).then(r => setSC(r.data)).catch(() => {}),
      Promise.all(jobIds.map(id => axios.get(`${API}/jobs/${id}`).then(r => r.data).catch(() => null)))
        .then(results => setJobs(results.filter(Boolean)))
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-slate-500">{error}</p>
    </div>
  );

  const companyName = sc?.name || "XSTRONG POWER EV CHARGER";

  return (
    <>
      {/* Print toolbar */}
      <div className="no-print fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <img src="/logo.jpg" alt="logo" style={{height:"36px",width:"auto",objectFit:"contain"}} />
          <div>
            <p className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>{companyName}</p>
            <p className="text-xs text-slate-400">{jobs.length} job card{jobs.length!==1?"s":""} ready to print</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => window.history.back()}
            className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1.5 border border-slate-200 rounded-lg">
            &#8592; Back
          </button>
          <button onClick={() => window.print()}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-5 py-2 rounded-xl transition-colors"
            data-testid="bulk-print-button">
            &#128424; Print All ({jobs.length})
          </button>
        </div>
      </div>

      {/* Job cards */}
      <div style={{paddingTop:"68px"}}>
        {jobs.map((job, idx) => (
          <div key={job.id}
            style={{maxWidth:"794px",margin:"0 auto",padding:"20px 24px",
              pageBreakAfter: idx < jobs.length-1 ? "always" : "auto",
              breakAfter:     idx < jobs.length-1 ? "page" : "auto"
            }}>

            {/* Header */}
            <div style={{borderBottom:"2px solid #16a34a",paddingBottom:"12px",marginBottom:"14px",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
                <img src="/logo.jpg" alt="logo" style={{height:"48px",width:"auto",objectFit:"contain"}} />
                <div>
                  <h1 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:"18px",fontWeight:700,color:"#16a34a",margin:0}}>{companyName}</h1>
                  <p style={{fontSize:"11px",color:"#64748b",margin:"2px 0"}}>{sc?.tagline || "EV Charger Repair & Service Center"}</p>
                  {sc?.phone && <p style={{fontSize:"10px",color:"#64748b",margin:0}}>&#128222; {sc.phone}{sc.phone2?` / ${sc.phone2}`:""}</p>}
                  {sc?.email && <p style={{fontSize:"10px",color:"#64748b",margin:0}}>&#9993; {sc.email}</p>}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <p style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:"18px",fontWeight:700,color:"#1e293b",margin:0}}>{job.job_no}</p>
                <p style={{fontSize:"10px",color:"#64748b",margin:"3px 0"}}>Date: {fmtDate(job.created_at)}</p>
                <span style={{fontSize:"11px",fontWeight:600,padding:"2px 10px",borderRadius:"20px",background:"#f0fdf4",color:"#16a34a",border:"1px solid #bbf7d0"}}>
                  {STATUS_EMOJI[job.current_status]||"🔄"} {job.current_status}
                </span>
              </div>
            </div>

            {/* Barcode row */}
            <div style={{display:"flex",alignItems:"center",gap:"20px",marginBottom:"14px",padding:"10px",border:"1px solid #e2e8f0",borderRadius:"8px"}}>
              <div>
                <p style={{fontSize:"9px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em",margin:"0 0 4px"}}>Shipment Barcode</p>
                <Barcode value={job.job_no} width={1.4} height={36} fontSize={10} margin={3} background="#ffffff" />
              </div>
              <div style={{width:"1px",height:"50px",background:"#e2e8f0"}}/>
              <div>
                <p style={{fontSize:"9px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em",margin:"0 0 4px"}}>QR Track</p>
                <QRCodeSVG value={`${window.location.origin}/track?job=${job.job_no}`} size={46} level="M" includeMargin={false}/>
              </div>
              <div>
                <p style={{fontSize:"10px",color:"#64748b",margin:0}}>Scan QR to track repair status online</p>
                <p style={{fontSize:"9px",color:"#94a3b8",margin:"2px 0 0"}}>Attach barcode to the device/packaging</p>
              </div>
            </div>

            {/* Customer + Chargers side by side */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:"14px",marginBottom:"14px"}}>
              <div style={{border:"1px solid #e2e8f0",borderRadius:"8px",padding:"12px"}}>
                <p style={{fontSize:"9px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.04em",margin:"0 0 8px"}}>Customer</p>
                {[
                  ["Name",    job.customer?.name],
                  ["Phone",   [job.customer?.phone, job.customer?.alt_phone].filter(Boolean).join(" / ")],
                  ["Address", [job.customer?.address, job.customer?.landmark ? `Near: ${job.customer.landmark}` : null, job.customer?.city, job.customer?.state, job.customer?.pincode].filter(Boolean).join(", ")],
                ].map(([l,v])=>(
                  <div key={l} style={{display:"flex",gap:"6px",marginBottom:"4px"}}>
                    <span style={{fontSize:"10px",color:"#94a3b8",minWidth:"50px",flexShrink:0}}>{l}:</span>
                    <span style={{fontSize:"11px",color:"#1e293b"}}>{v||"—"}</span>
                  </div>
                ))}
              </div>
              <div style={{border:"1px solid #e2e8f0",borderRadius:"8px",padding:"12px"}}>
                <p style={{fontSize:"9px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.04em",margin:"0 0 8px"}}>
                  Charger Details ({(job.products||[]).length} unit{(job.products||[]).length!==1?"s":""})
                </p>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead>
                    <tr style={{borderBottom:"1px solid #f1f5f9"}}>
                      {["#","Battery Cut-off / Amp","Serial No.","Purchase Date","Body Type"].map(h=>(
                        <th key={h} style={{textAlign:"left",fontSize:"9px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",padding:"2px 6px"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(job.products||[]).map((p,i)=>(
                      <tr key={`${job.id}-prod-${i}`} style={{borderBottom:i<(job.products||[]).length-1?"1px solid #f8fafc":"none"}}>
                        <td style={{padding:"3px 6px",fontSize:"10px",color:"#94a3b8"}}>{i+1}</td>
                        <td style={{padding:"3px 6px",fontSize:"11px",fontFamily:"monospace",fontWeight:700,color:"#16a34a"}}>{p.charger_spec||"—"}</td>
                        <td style={{padding:"3px 6px",fontSize:"10px",fontFamily:"monospace",color:"#1e293b"}}>{p.serial_number||"—"}</td>
                        <td style={{padding:"3px 6px",fontSize:"11px",color:"#1e293b"}}>{p.purchase_date||"—"}</td>
                        <td style={{padding:"3px 6px",fontSize:"10px",color:"#1e293b"}}>{p.body_type||"—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Fault + Status history */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"14px",marginBottom:"14px"}}>
              <div style={{border:"1px solid #e2e8f0",borderRadius:"8px",padding:"12px"}}>
                <p style={{fontSize:"9px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",margin:"0 0 6px"}}>Fault Description</p>
                <p style={{fontSize:"11px",color:job.specs?.fault_description?"#1e293b":"#94a3b8",fontStyle:job.specs?.fault_description?"normal":"italic",margin:0,lineHeight:1.4}}>
                  {job.specs?.fault_description||"Not specified"}
                </p>
              </div>
              <div style={{border:"1px solid #e2e8f0",borderRadius:"8px",padding:"12px"}}>
                <p style={{fontSize:"9px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",margin:"0 0 6px"}}>Service History</p>
                {(job.status_history||[]).slice(-4).map((e,i)=>(
                  <div key={`${e.status}-${e.timestamp || i}`} style={{display:"flex",alignItems:"flex-start",gap:"6px",marginBottom:"4px"}}>
                    <div style={{width:"6px",height:"6px",borderRadius:"50%",marginTop:"3px",flexShrink:0,background:STATUS_DOT[e.status]||"#94a3b8"}}/>
                    <div>
                      <span style={{fontSize:"10px",fontWeight:600,color:"#1e293b"}}>{e.status}</span>
                      <span style={{fontSize:"9px",color:"#94a3b8",marginLeft:"6px"}}>{fmtDT(e.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment + Signatures */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"14px"}}>
              <div style={{border:"1px solid #e2e8f0",borderRadius:"8px",padding:"12px"}}>
                <p style={{fontSize:"9px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",margin:"0 0 6px"}}>Payment</p>
                {[["Total",`\u20b9${(job.payment?.amount||0).toLocaleString("en-IN")}`],["Paid",`\u20b9${(job.payment?.paid_amount||0).toLocaleString("en-IN")}`],["Due",`\u20b9${(job.payment?.due_amount||0).toLocaleString("en-IN")}`]].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",marginBottom:"3px"}}>
                    <span style={{fontSize:"10px",color:"#64748b"}}>{l}</span>
                    <span style={{fontSize:"11px",fontWeight:600,color:"#1e293b",fontFamily:"monospace"}}>{v}</span>
                  </div>
                ))}
                <p style={{fontSize:"10px",color:"#94a3b8",marginTop:"4px"}}>Method: {job.payment?.method||"Cash"}</p>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
                {["Customer Signature","Authorized Signature"].map(l=>(
                  <div key={l}>
                    <div style={{height:"36px",borderBottom:"1px solid #cbd5e1"}}/>
                    <p style={{fontSize:"9px",color:"#94a3b8",marginTop:"4px"}}>{l}</p>
                  </div>
                ))}
              </div>
            </div>

            {idx < jobs.length-1 && (
              <div style={{textAlign:"center",marginTop:"10px"}}>
                <p style={{fontSize:"9px",color:"#cbd5e1"}}>--- {companyName} --- continued on next page ---</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
